import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { adminUserService } from '../../services/adminUserService';
import {
  AdminUser,
  USER_ROLES,
  UserRole,
  UserStatus,
  adminUserName,
} from '../../types/adminUser';
import { ApiError } from '../../types/api';

/** Loud, operator-facing message for the failure modes this screen can hit. */
const describeError = (err: unknown, fallback: string): string => {
  const status = (err as ApiError)?.status;
  switch (status) {
    case 401:
      return 'Your session has expired — sign in again.';
    case 403:
      return 'You do not have permission to manage users.';
    case 404:
      return 'That user no longer exists, or does not hold that role. The list has been refreshed.';
    case 409:
      return 'You cannot deactivate your own account or revoke your own admin role — ask another admin.';
    default:
      return `${fallback}: ${(err as ApiError)?.message ?? String(err)}`;
  }
};

/** Renders a login stamp, or a clear "never" for an account that has not logged in. */
const formatLastLogin = (iso: string | null): string => {
  if (!iso) return 'Never';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? 'Unknown' : at.toLocaleString();
};

const hasRole = (user: AdminUser, role: UserRole): boolean =>
  (user.roles ?? []).includes(role);

/**
 * Admin-only user administration (issue #379): every account with its email,
 * roles, last login and status, plus controls to grant or revoke a role and to
 * deactivate or reactivate the account.
 *
 * Mutations are optimistically re-read rather than patched locally, so the table
 * always shows what the server actually stored — including a change another
 * admin made in parallel.
 */
export const UserAdminPanel: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busySubject, setBusySubject] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { users: list } = await adminUserService.list(200, 0);
      setUsers(list);
      return true;
    } catch (err) {
      setError(describeError(err, 'Could not load users'));
      setUsers([]);
      return false;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Runs one mutation, then re-reads the table so it reflects stored state. */
  const mutate = async (
    subject: string,
    action: () => Promise<void>,
    fallback: string
  ) => {
    setBusySubject(subject);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(describeError(err, fallback));
    } finally {
      await refresh();
      setBusySubject(null);
    }
  };

  const onToggleRole = (user: AdminUser, role: UserRole, grant: boolean) =>
    mutate(
      user.subject,
      () =>
        grant
          ? adminUserService.assignRole(user.subject, role)
          : adminUserService.revokeRole(user.subject, role),
      `Could not ${grant ? 'grant' : 'revoke'} the ${role} role`
    );

  const onToggleStatus = (user: AdminUser, next: UserStatus) =>
    mutate(
      user.subject,
      () => adminUserService.setStatus(user.subject, next),
      `Could not ${next === 'active' ? 'reactivate' : 'deactivate'} the account`
    );

  if (users === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress aria-label="Loading users" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, overflow: 'auto' }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Users
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Accounts are created automatically on first sign-in. Deactivating an account
        blocks it at the next request.
      </Typography>

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {users.length === 0 && !error ? (
        <Alert severity="info">
          No accounts yet — they appear here after their first sign-in.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="User accounts">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const name = adminUserName(user);
                const active = user.status === 'active';
                const busy = busySubject === user.subject;
                return (
                  <TableRow key={user.subject} hover>
                    <TableCell>{name}</TableCell>
                    <TableCell>{user.email || '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {USER_ROLES.map((role) => {
                          const held = hasRole(user, role);
                          return (
                            <Tooltip
                              key={role}
                              title={`${held ? 'Revoke' : 'Grant'} the ${role} role for ${name}`}
                            >
                              <Chip
                                label={role}
                                size="small"
                                // Never rely on colour alone: a held role is
                                // filled and ticked, an unheld one outlined.
                                icon={<span aria-hidden="true">{held ? '✓' : '+'}</span>}
                                color={held ? 'primary' : 'default'}
                                variant={held ? 'filled' : 'outlined'}
                                disabled={busy}
                                aria-label={`${held ? 'Revoke' : 'Grant'} ${role} role for ${name}`}
                                onClick={() => void onToggleRole(user, role, !held)}
                              />
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatLastLogin(user.last_login_at)}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={active}
                            disabled={busy}
                            inputProps={{
                              'aria-label': `${active ? 'Deactivate' : 'Reactivate'} ${name}`,
                            }}
                            onChange={() =>
                              void onToggleStatus(user, active ? 'disabled' : 'active')
                            }
                          />
                        }
                        label={active ? 'Active' : 'Disabled'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
